import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';

export type PageStatus = 'loading' | 'empty' | 'error' | 'accessDenied' | 'unavailable' | 'success';

export interface PageStateProps {
  readonly children?: ReactNode;
  readonly emptyMessage?: string;
  readonly error?: Pick<AppError, 'message' | 'requestId'> | null;
  readonly onRetry?: () => void;
  readonly status: PageStatus;
}

export function PageState({ children, emptyMessage, error, onRetry, status }: PageStateProps) {
  // Every string here is translated. This component renders on 60-odd pages, and the application
  // defaults to Thai, so a hardcoded literal would show English to the primary audience on every load,
  // every empty list, and every failure. `emptyMessage` stays overridable for callers with better copy.
  const { t } = useTranslation();

  if (status === 'success') return <>{children}</>;
  if (status === 'loading') {
    return (
      <Stack aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', py: 4 }}>
        <CircularProgress size={20} />
        <Typography>{t('common.loading')}</Typography>
      </Stack>
    );
  }
  if (status === 'empty')
    return (
      <Typography color="text.secondary" sx={{ py: 4 }}>
        {emptyMessage ?? t('pageState.noRecords')}
      </Typography>
    );
  if (status === 'accessDenied')
    return <Alert severity="error">{t('pageState.accessDenied')}</Alert>;
  if (status === 'unavailable')
    return <Alert severity="warning">{t('pageState.unavailable')}</Alert>;

  return (
    <Box>
      <Alert severity="error">
        {error?.message ?? t('pageState.error')}
        {error?.requestId === null || error?.requestId === undefined
          ? null
          : ` ${t('pageState.requestId', { id: error.requestId })}`}
      </Alert>
      {onRetry === undefined ? null : (
        <Button onClick={onRetry} sx={{ mt: 2 }}>
          {t('pageState.retry')}
        </Button>
      )}
    </Box>
  );
}
